import { createContext } from 'react';
import { Business } from '../business';

/**
 * Context type for managing business data within the application.
 *
 * @property {Business[]} businesses - The list of all businesses.
 * @property {boolean} loading - Indicates if business data is currently being loaded.
 * @property {string | null} error - Error message if an operation fails, otherwise null.
 * @property {(id: string) => Promise<Business | null>} getBusinessById - Fetches a business by its unique identifier.
 * @property {(business: Omit<Business, 'id' | 'createdAt' | 'updatedAt' | 'rating' | 'completedJobs'>) => Promise<void>} createBusiness - Creates a new business with the provided data.
 * @property {(businessId: string, businessData: Partial<Business>) => Promise<void>} updateBusiness - Updates an existing business with the given partial data.
 * @property {(businessId: string) => Promise<void>} deleteBusiness - Deletes a business by its unique identifier.
 * @property {() => Promise<void>} refreshBusinesses - Refreshes the list of businesses from the data source.
 * @property {(postalCode: string) => Promise<Business[]>} getBusinessesByPostalCode - Efficiently fetches businesses that service a specific Postal code using database index.
 * @property {(postalCode: string, serviceName: string) => Promise<Business[]>} getBusinessesByPostalCodeAndService - Fetches businesses that service a specific Postal code AND offer a specific service.
 */
export interface BusinessDataContextType {
  businesses: Business[];
  loading: boolean;
  error: string | null;
  getBusinessById: (id: string) => Promise<Business | null>;
  getBusinessesByPostalCode: (postalCode: string) => Promise<Business[]>;
  getBusinessesByPostalCodeAndService: (postalCode: string, serviceName: string) => Promise<Business[]>;
  createBusiness: (business: Omit<Business, 'id' | 'createdAt' | 'updatedAt' | 'rating' | 'completedJobs'>) => Promise<void>;
  updateBusiness: (businessId: string, businessData: Partial<Business>) => Promise<void>;
  deleteBusiness: (businessId: string) => Promise<void>;
  refreshBusinesses: () => Promise<void>;
}

/**
 * React context for providing and consuming business-related data throughout the component tree.
 * 
 * @remarks
 * This context should be used with a corresponding provider that supplies a value of type `BusinessDataContextType`.
 * If the context is accessed outside of its provider, it will be `undefined`.
 *
 * @see BusinessDataContextType
 */
export const BusinessContext = createContext<BusinessDataContextType | undefined>(undefined);